import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Baby,
  Building2,
  Users,
  Target,
  Award,
  Shield,
  Heart,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  FileText
} from 'lucide-react';

export const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center">
                <Baby className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                NeoBreed AI
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-slate-600 hover:text-slate-900 transition-colors">Ana Sayfa</a>
              <a href="/about" className="text-[#5A67D8] font-medium">Hakkımızda</a>
              <a href="/contact" className="text-slate-600 hover:text-slate-900 transition-colors">İletişim</a>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-slate-600"
              >
                Giriş Yap
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-[#5A67D8] hover:bg-[#4338CA] text-white"
              >
                Ücretsiz Başla
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#F8F7FC] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E0D7FF] rounded-full mb-6">
            <Building2 className="w-4 h-4 text-[#5A67D8]" />
            <span className="text-sm font-medium text-[#5A67D8]">Kurumsal</span>
          </div>
          <h1 
            className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Hakkımızda
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            NeoBreed AI, yapay zeka teknolojisi ile fetal görüntüleme alanında öncü çözümler sunan bir sağlık teknolojisi şirketidir.
          </p>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-slate-100 shadow-lg overflow-hidden">
            <CardContent className="p-8">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    KONSÜLTAN ÖZEL SAĞLIK HİZMETLERİ
                  </h2>
                  <p className="text-slate-600 font-medium">
                    GIDA HAYV. TURZ. SAN. VE TİC. LTD. ŞTİ.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 pt-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#5A67D8] mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-800">Adres</p>
                        <p className="text-slate-600 text-sm">
                          Emekevler Mah. D-100 Karayolu Cad.<br />
                          Neu Plaza No: 84/3<br />
                          Kartepe / KOCAELİ
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-[#5A67D8] mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-800">Vergi Bilgileri</p>
                        <p className="text-slate-600 text-sm">
                          Vergi Dairesi: Alemdar V.D.<br />
                          Vergi No: 576 051 2362
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-slate-100 shadow-lg">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-[#E0D7FF] flex items-center justify-center mb-6">
                  <Target className="w-6 h-6 text-[#5A67D8]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Misyonumuz
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Anne-baba adaylarına bebeklerinin yüzünü doğmadan önce görme imkanı sunarak, 
                  hamilelik sürecini daha özel ve unutulmaz kılmak. Yapay zeka teknolojisini 
                  sağlık sektöründe etik ve güvenilir bir şekilde kullanarak, ailelere değer katmak.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-100 shadow-lg">
              <CardContent className="p-8">
                <div className="w-12 h-12 rounded-xl bg-[#FEE2E2] flex items-center justify-center mb-6">
                  <Heart className="w-6 h-6 text-[#EC4899]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Vizyonumuz
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Türkiye'nin ve bölgenin lider fetal görüntüleme AI platformu olmak. 
                  Her anne-babanın bebeğiyle olan bağını güçlendirecek teknolojiler geliştirmek 
                  ve sağlık profesyonellerine en ileri araçları sunmak.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Değerlerimiz
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              NeoBreed AI olarak, tüm faaliyetlerimizde bu temel değerlere bağlı kalıyoruz.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Güvenlik & Gizlilik',
                description: 'Tüm veriler KVKK uyumlu sistemlerde şifrelenmiş olarak saklanır.',
                color: 'bg-blue-100 text-blue-600'
              },
              {
                icon: Award,
                title: 'Kalite & Doğruluk',
                description: 'En gelişmiş AI modelleri ile yüksek doğrulukta sonuçlar üretiyoruz.',
                color: 'bg-purple-100 text-purple-600'
              },
              {
                icon: Users,
                title: 'Müşteri Odaklılık',
                description: 'Her aileye özel deneyim sunmak için sürekli gelişim içindeyiz.',
                color: 'bg-pink-100 text-pink-600'
              }
            ].map((value, index) => (
              <Card key={index} className="border-2 border-slate-100 hover:border-[#5A67D8]/30 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className={`w-12 h-12 rounded-xl ${value.color} flex items-center justify-center mx-auto mb-4`}>
                    <value.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#5A67D8] to-[#4338CA]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bizimle İletişime Geçin
          </h2>
          <p className="text-white/80 mb-8">
            Sorularınız veya işbirliği talepleriniz için bize ulaşın.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              onClick={() => navigate('/contact')}
              className="bg-white text-[#5A67D8] hover:bg-slate-100"
            >
              İletişim Sayfası
            </Button>
            <Button
              onClick={() => navigate('/register')}
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Ücretsiz Başla
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5A67D8] to-[#4338CA] flex items-center justify-center">
              <Baby className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold">NeoBreed AI</span>
          </div>
          <p className="text-slate-400 text-sm text-center">
            © 2026 Konsültan Özel Sağlık Hizmetleri Ltd. Şti. Tüm hakları saklıdır.
          </p>
          <div className="flex gap-6">
            <a href="/about" className="text-slate-400 hover:text-white text-sm transition-colors">Hakkımızda</a>
            <a href="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">İletişim</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
